import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, Heart, MessageSquare, TrendingUp, Info, Lock, Star, Users, Loader } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ProviderStatistics = () => {
  const [stats, setStats] = useState({
    profile_views_total: 0,
    profile_views_30d: 0,
    favorite_count_total: 0,
    total_inquiries: 0,
    successful_referrals: 0
  });
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load both statistics and profile data
      const [statsResponse, profileResponse] = await Promise.all([
        axios.get(`${API}/api/provider/statistics`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/provider/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStats(statsResponse.data);
      setProfile(profileResponse.data);
      setError(null);
    } catch (error) {
      console.error('Error loading statistics:', error);
      
      // Only show toast for non-404 errors
      if (error.response && error.response.status !== 404) {
        setError('Statistiken konnten nicht geladen werden. Bitte versuche es später erneut.');
      } else {
        setError(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const getPlanLevel = () => {
    return profile?.plan_level || 'basic';
  };

  const getPlanName = () => {
    const level = getPlanLevel();
    const planNames = {
      'basic': 'Basic',
      'plus': 'Plus',
      'pro': 'Pro'
    };
    return planNames[level] || 'Basic';
  };

  const canAccessPlusFeatures = () => {
    const level = getPlanLevel();
    return level === 'plus' || level === 'pro';
  };

  const canAccessProFeatures = () => {
    const level = getPlanLevel();
    return level === 'pro';
  };

  const StatCard = ({ icon: Icon, title, value, description, color, locked, requiredPlan }) => (
    <div className={`bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow ${locked ? 'opacity-60 relative' : ''}`}>
      {locked && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-2xl flex items-center justify-center z-10">
          <div className="text-center px-6">
            <Lock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-700 mb-1">
              {requiredPlan}-Feature
            </p>
            <p className="text-xs text-gray-600">
              Upgrade erforderlich
            </p>
          </div>
        </div>
      )}
      
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="group relative">
          <Info className="w-5 h-5 text-gray-400 cursor-help" />
          <div className="absolute right-0 top-8 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 shadow-lg">
            {description}
            <div className="absolute -top-2 right-4 w-3 h-3 bg-gray-900 transform rotate-45"></div>
          </div>
        </div>
      </div>
      
      <div className="mb-2">
        <div className="text-3xl font-bold text-gray-900">
          {loading ? (
            <div className="animate-pulse bg-gray-200 h-9 w-20 rounded"></div>
          ) : locked ? (
            <Lock className="w-8 h-8 text-gray-300" />
          ) : (
            (value ?? 0).toLocaleString('de-DE')
          )}
        </div>
      </div>
      
      <div className="text-sm text-gray-600 font-medium">
        {title}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-[#2B2142]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Plan Badge */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Profil-Statistiken</h2>
          <p className="text-sm text-gray-600">
            Anonymisierte Kennzahlen zu deinem Profil
          </p>
        </div>
        
        {profile && (
          <div className={`px-4 py-2 rounded-xl font-semibold text-sm flex items-center gap-2 ${
            getPlanLevel() === 'pro' ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' :
            getPlanLevel() === 'plus' ? 'bg-gradient-to-r from-[#2B2142] to-[#3D3158] text-white' :
            'bg-gray-200 text-gray-700'
          }`}>
            <Star className="w-4 h-4" />
            {getPlanName()} Plan
          </div>
        )}
      </div>

      {/* Error Message (only for real errors, not 404) */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-900 mb-1">Fehler beim Laden</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {/* Plus/Pro Feature - Profile Views Total */}
        <StatCard
          icon={Eye}
          title="Profilaufrufe gesamt"
          value={stats.profile_views_total}
          description="Anzahl der Aufrufe deiner öffentlichen Profilseite. Eigene Aufrufe werden nicht gezählt."
          color="bg-[#2B2142]"
          locked={!canAccessPlusFeatures()}
          requiredPlan="Plus"
        />

        {/* Plus/Pro Feature - Favorites */}
        <StatCard
          icon={Heart}
          title="Als Favorit markiert"
          value={stats.favorite_count_total}
          description="So oft wurde dein Profil von registrierten Nutzern als Favorit gespeichert."
          color="bg-red-500"
          locked={!canAccessPlusFeatures()}
          requiredPlan="Plus"
        />

        {/* Plus/Pro Feature - 30-day views */}
        <StatCard
          icon={TrendingUp}
          title="Aufrufe (30 Tage)"
          value={stats.profile_views_30d}
          description="Profilaufrufe der letzten 30 Tage. Hilft dir, aktuelle Trends zu erkennen."
          color="bg-purple-500"
          locked={!canAccessPlusFeatures()}
          requiredPlan="Plus"
        />

        {/* TODO: Re-enable when service booking is available
        <StatCard
          icon={MessageSquare}
          title="Anfragen gesamt"
          value={stats.total_inquiries}
          description="Gesamtanzahl der Kontaktanfragen, die du über EventConnect erhalten hast."
          color="bg-green-500"
          locked={!canAccessPlusFeatures()}
          requiredPlan="Plus"
        />
        */}

        {/* Pro Feature - Referrals */}
        <StatCard
          icon={Users}
          title="Erfolgreiche Empfehlungen"
          value={stats.successful_referrals}
          description="Anzahl der Dienstleister, die du erfolgreich geworben hast und die nun aktiv auf EventConnect sind."
          color="bg-orange-500"
          locked={!canAccessProFeatures()}
          requiredPlan="Pro"
        />
      </div>

      {/* Upgrade CTA for Basic users */}
      {getPlanLevel() === 'basic' && (
        <div className="bg-gradient-to-r from-[#2B2142] to-[#3D3158] rounded-2xl p-6 text-white">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <TrendingUp className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-xl mb-2">
                Schalte erweiterte Statistiken frei
              </h3>
              <p className="text-white/90 mb-4">
                Mit <strong>Plus</strong> oder <strong>Pro</strong> erhältst du Zugriff auf:
              </p>
              <ul className="text-sm text-white/90 space-y-2 mb-4 list-disc list-inside">
                <li>30-Tage-Trend-Analysen deiner Profilaufrufe</li>
                {/* TODO: Re-enable when service booking is available */}
                {/* <li>Detaillierte Anfrage-Statistiken</li> */}
                <li>Besseres Ranking in der Suche</li>
                <li>Mehr Sichtbarkeit für dein Profil</li>
              </ul>
              <button
                onClick={() => window.location.href = '/pricing'}
                className="bg-white text-[#2B2142] px-6 py-2 rounded-lg font-semibold hover:bg-[#FFF0D1]/30 transition-colors"
              >
                Jetzt upgraden
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-[#FFF0D1]/30 border-2 border-[#2B2142]/20 rounded-2xl p-6">
        <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
          <Info className="w-5 h-5 text-[#2B2142]" />
          Datenschutz & Anonymität
        </h3>
        <div className="text-sm text-gray-700 space-y-2">
          <p>
            Du siehst hier nur aggregierte Zahlen (Views, Favoriten). 
            Es werden <strong>keine persönlichen Daten</strong> der Nutzer angezeigt, die dein Profil aufgerufen 
            oder favorisiert haben.
          </p>
          <p>
            Diese Statistiken helfen dir zu verstehen, wie gut dein Profil bei potenziellen Kunden ankommt.
          </p>
        </div>
      </div>

      {/* Tips for improvement */}
      {!loading && (stats.profile_views_total < 50 || stats.favorite_count_total < 5) && (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-6">
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-yellow-600" />
            Tipps für mehr Sichtbarkeit
          </h3>
          <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
            <li>Füge hochwertige Bilder zu deinem Profil hinzu</li>
            <li>Vervollständige deine Profilbeschreibung</li>
            <li>Sammle Bewertungen von zufriedenen Kunden</li>
            <li>Reagiere schnell auf Anfragen</li>
            <li>Nutze klare und transparente Preise</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProviderStatistics;
